export interface SportConfig {
  id: string;
  name: string;
  displayName: string;
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  sportIcon: string;
  description: string;
  features: string[];
  content: SportContent;
  branding: SportBranding;
}

export interface SportContent {
  drills: Drill[];
  strategies: Strategy[];
  equipment: Equipment[];
  fundamentals: Fundamental[];
  skillAssessment: SkillAssessmentQuestion[];
  aiPrompts: AIPrompt[];
}

export interface SportBranding {
  logo: string;
  heroImage: string;
  colorScheme: ColorScheme;
  typography: Typography;
}

export interface ColorScheme {
  primary: string;
  secondary: string;
  accent: string;
  background: string;
  surface: string;
  text: string;
  textSecondary: string;
  success: string;
  warning: string;
  error: string;
  border: string;
}

export interface Typography {
  fontFamily: string;
  headingFont: string;
  bodyFont: string;
}

export interface Drill {
  id: string;
  title: string;
  description: string;
  difficulty: 'beginner' | 'intermediate' | 'advanced';
  duration: number; // minutes
  equipment: string[];
  instructions: string[];
  tips: string[];
}

export interface Strategy {
  id: string;
  title: string;
  description: string;
  level: 'beginner' | 'intermediate' | 'advanced';
  situation: string;
  execution: string[];
  commonMistakes: string[];
}

export interface Equipment {
  id: string;
  name: string;
  description: string;
  price: string;
  recommendations: string[];
  maintenance: string[];
}

export interface Fundamental {
  id: string;
  title: string;
  description: string;
  importance: 'critical' | 'important' | 'helpful';
  steps: string[];
  practiceTips: string[];
}

export interface SkillAssessmentQuestion {
  id: string;
  question: string;
  options: string[];
  correctAnswer: number;
  explanation: string;
  skill: string;
}

export interface AIPrompt {
  id: string;
  title: string;
  prompt: string;
  context: string;
  expectedOutput: string;
}

// Pickleball Configuration
export const pickleballConfig: SportConfig = {
  id: 'pickleball',
  name: 'Pickleball',
  displayName: 'Pickleball Coach',
  primaryColor: '#FF6B35',
  secondaryColor: '#FF8C42',
  accentColor: '#FFA366',
  sportIcon: '🏓',
  description: 'Master pickleball with AI-powered coaching, video analysis, and personalized training plans.',
  features: [
    'video-analysis',
    'skill-assessment',
    'drill-library',
    'strategy-guide',
    'equipment-recommendations',
    'fundamentals-training',
    'ai-coaching',
    'progress-tracking'
  ],
  content: {
    drills: [
      {
        id: 'pb-drill-1',
        title: 'Dink Practice',
        description: 'Practice controlled dinking to improve touch and placement',
        difficulty: 'beginner',
        duration: 15,
        equipment: ['paddle', 'ball', 'net'],
        instructions: [
          'Stand 7 feet from the net',
          'Practice soft, controlled shots',
          'Aim for the kitchen area',
          'Focus on consistency over power'
        ],
        tips: [
          'Keep your paddle face open',
          'Use your wrist, not your arm',
          'Stay balanced and ready to move'
        ]
      },
      {
        id: 'pb-drill-2',
        title: 'Third Shot Drop',
        description: 'Master the crucial third shot that sets up the point',
        difficulty: 'intermediate',
        duration: 20,
        equipment: ['paddle', 'ball', 'net', 'partner'],
        instructions: [
          'Start from the baseline',
          'Hit a soft, high-arcing shot',
          'Aim for the kitchen line',
          'Move forward after the shot'
        ],
        tips: [
          'Use a continental grip',
          'Keep your head down through contact',
          'Follow through toward your target'
        ]
      }
    ],
    strategies: [
      {
        id: 'pb-strategy-1',
        title: 'Kitchen Line Play',
        description: 'Control the kitchen line to dominate the game',
        level: 'intermediate',
        situation: 'When both teams are at the kitchen line',
        execution: [
          'Stay patient and wait for the right ball',
          'Use soft shots to create openings',
          'Communicate with your partner',
          'Be ready to poach when appropriate'
        ],
        commonMistakes: [
          'Hitting too hard from the kitchen',
          'Not communicating with partner',
          'Getting impatient and forcing shots'
        ]
      }
    ],
    equipment: [
      {
        id: 'pb-equipment-1',
        name: 'Composite Paddle',
        description: 'High-quality composite paddle for intermediate players',
        price: '$80-150',
        recommendations: [
          'Choose weight that feels comfortable',
          'Consider grip size for your hand',
          'Look for good balance and control'
        ],
        maintenance: [
          'Clean after each use',
          'Store in a protective case',
          'Check for damage regularly'
        ]
      }
    ],
    fundamentals: [
      {
        id: 'pb-fundamental-1',
        title: 'Ready Position',
        description: 'The foundation of all pickleball movement and shots',
        importance: 'critical',
        steps: [
          'Feet shoulder-width apart',
          'Knees slightly bent',
          'Weight on the balls of your feet',
          'Paddle in front, ready position'
        ],
        practiceTips: [
          'Practice this position daily',
          'Stay in ready position between shots',
          'Make it your default stance'
        ]
      }
    ],
    skillAssessment: [
      {
        id: 'pb-assessment-1',
        question: 'What is the most important shot in pickleball?',
        options: [
          'The serve',
          'The third shot drop',
          'The smash',
          'The dink'
        ],
        correctAnswer: 1,
        explanation: 'The third shot drop is crucial because it allows your team to move to the kitchen line and control the point.',
        skill: 'strategy'
      }
    ],
    aiPrompts: [
      {
        id: 'pb-ai-1',
        title: 'Video Analysis',
        prompt: 'Analyze this pickleball video and provide specific feedback on form, strategy, and areas for improvement.',
        context: 'User uploads a video of their pickleball game or practice session',
        expectedOutput: 'Detailed analysis with specific tips, drills, and improvement areas'
      }
    ]
  },
  branding: {
    logo: '/assets/pickleball-logo.png',
    heroImage: '/assets/pickleball-hero.jpg',
    colorScheme: {
      primary: '#FF6B35',
      secondary: '#FF8C42',
      accent: '#FFA366',
      background: '#FFFFFF',
      surface: '#F8F9FA',
      text: '#2C3E50',
      textSecondary: '#6C757D',
      success: '#28A745',
      warning: '#FFC107',
      error: '#DC3545',
      border: '#E9ECEF'
    },
    typography: {
      fontFamily: 'Inter, system-ui, sans-serif',
      headingFont: 'Inter, system-ui, sans-serif',
      bodyFont: 'Inter, system-ui, sans-serif'
    }
  }
};

// Tennis Configuration
export const tennisConfig: SportConfig = {
  id: 'tennis',
  name: 'Tennis',
  displayName: 'Tennis Coach',
  primaryColor: '#1E40AF',
  secondaryColor: '#3B82F6',
  accentColor: '#FBBF24',
  sportIcon: '🎾',
  description: 'Elevate your tennis game with AI coaching, technique analysis, and strategic insights.',
  features: [
    'video-analysis',
    'skill-assessment',
    'drill-library',
    'strategy-guide',
    'equipment-recommendations',
    'fundamentals-training',
    'ai-coaching',
    'progress-tracking'
  ],
  content: {
    drills: [
      {
        id: 'tennis-drill-1',
        title: 'Forehand Crosscourt',
        description: 'Practice hitting forehands crosscourt for consistency and control',
        difficulty: 'beginner',
        duration: 20,
        equipment: ['racket', 'balls', 'net'],
        instructions: [
          'Stand in ready position',
          'Use proper footwork to get in position',
          'Focus on clean contact',
          'Follow through toward your target'
        ],
        tips: [
          'Keep your eye on the ball',
          'Use your legs for power',
          'Stay balanced throughout the stroke'
        ]
      }
    ],
    strategies: [
      {
        id: 'tennis-strategy-1',
        title: 'Serve and Volley',
        description: 'Aggressive strategy to take control of the point early',
        level: 'advanced',
        situation: 'When you have a strong serve and good volley skills',
        execution: [
          'Hit a strong first serve',
          'Move forward immediately after serving',
          'Be ready to volley the return',
          'Close the net aggressively'
        ],
        commonMistakes: [
          'Not moving forward quickly enough',
          'Poor volley technique',
          'Getting caught in no-man\'s land'
        ]
      }
    ],
    equipment: [
      {
        id: 'tennis-equipment-1',
        name: 'Tennis Racket',
        description: 'Professional tennis racket for competitive play',
        price: '$150-300',
        recommendations: [
          'Choose appropriate head size',
          'Consider string tension',
          'Test grip size and weight'
        ],
        maintenance: [
          'Restring regularly',
          'Clean grip after use',
          'Store in protective case'
        ]
      }
    ],
    fundamentals: [
      {
        id: 'tennis-fundamental-1',
        title: 'Split Step',
        description: 'Essential movement pattern for tennis',
        importance: 'critical',
        steps: [
          'Start in ready position',
          'Jump slightly as opponent hits',
          'Land with feet apart',
          'Move toward the ball'
        ],
        practiceTips: [
          'Practice this movement daily',
          'Use it before every shot',
          'Make it automatic'
        ]
      }
    ],
    skillAssessment: [
      {
        id: 'tennis-assessment-1',
        question: 'What is the most important shot in tennis?',
        options: [
          'The serve',
          'The forehand',
          'The backhand',
          'The volley'
        ],
        correctAnswer: 0,
        explanation: 'The serve is the only shot you completely control and sets up every point.',
        skill: 'strategy'
      }
    ],
    aiPrompts: [
      {
        id: 'tennis-ai-1',
        title: 'Video Analysis',
        prompt: 'Analyze this tennis video and provide specific feedback on technique, footwork, and strategic improvements.',
        context: 'User uploads a video of their tennis match or practice session',
        expectedOutput: 'Detailed analysis with specific tips, drills, and improvement areas'
      }
    ]
  },
  branding: {
    logo: '/assets/tennis-logo.png',
    heroImage: '/assets/tennis-hero.jpg',
    colorScheme: {
      primary: '#1E40AF',
      secondary: '#3B82F6',
      accent: '#FBBF24',
      background: '#FFFFFF',
      surface: '#F8F9FA',
      text: '#2C3E50',
      textSecondary: '#6C757D',
      success: '#28A745',
      warning: '#FFC107',
      error: '#DC3545',
      border: '#E9ECEF'
    },
    typography: {
      fontFamily: 'Inter, system-ui, sans-serif',
      headingFont: 'Inter, system-ui, sans-serif',
      bodyFont: 'Inter, system-ui, sans-serif'
    }
  }
};

// Golf Configuration
export const golfConfig: SportConfig = {
  id: 'golf',
  name: 'Golf',
  displayName: 'Golf Coach',
  primaryColor: '#1B4332',
  secondaryColor: '#40916C',
  accentColor: '#D4AF37',
  sportIcon: '⛳',
  description: 'Perfect your golf game with AI-powered swing analysis, course strategy, and personalized coaching.',
  features: [
    'video-analysis',
    'skill-assessment',
    'drill-library',
    'strategy-guide',
    'equipment-recommendations',
    'fundamentals-training',
    'ai-coaching',
    'progress-tracking'
  ],
  content: {
    drills: [
      {
        id: 'golf-drill-1',
        title: 'Putting Gate Drill',
        description: 'Improve putting accuracy with gate alignment',
        difficulty: 'beginner',
        duration: 15,
        equipment: ['putter', 'golf balls', 'alignment sticks'],
        instructions: [
          'Set up two alignment sticks as a gate',
          'Practice putting through the gate',
          'Focus on straight back and through',
          'Keep your head still'
        ],
        tips: [
          'Use a pendulum motion',
          'Keep your eyes over the ball',
          'Don\'t look up too early'
        ]
      }
    ],
    strategies: [
      {
        id: 'golf-strategy-1',
        title: 'Course Management',
        description: 'Strategic approach to playing the course',
        level: 'intermediate',
        situation: 'Planning your approach to each hole',
        execution: [
          'Study the course layout',
          'Identify trouble spots',
          'Play to your strengths',
          'Take calculated risks'
        ],
        commonMistakes: [
          'Always going for the green',
          'Not considering wind and conditions',
          'Playing too aggressively'
        ]
      }
    ],
    equipment: [
      {
        id: 'golf-equipment-1',
        name: 'Golf Clubs',
        description: 'Complete set of golf clubs for all skill levels',
        price: '$500-2000',
        recommendations: [
          'Get fitted by a professional',
          'Choose appropriate shaft flex',
          'Consider your skill level'
        ],
        maintenance: [
          'Clean clubs after each round',
          'Check grips regularly',
          'Store in a dry place'
        ]
      }
    ],
    fundamentals: [
      {
        id: 'golf-fundamental-1',
        title: 'Grip',
        description: 'Foundation of a good golf swing',
        importance: 'critical',
        steps: [
          'Place club in left hand',
          'Wrap fingers around grip',
          'Add right hand with proper overlap',
          'Check pressure and alignment'
        ],
        practiceTips: [
          'Practice grip without a club',
          'Check grip before every shot',
          'Get feedback from a pro'
        ]
      }
    ],
    skillAssessment: [
      {
        id: 'golf-assessment-1',
        question: 'What is the most important club in your bag?',
        options: [
          'Driver',
          '7-iron',
          'Putter',
          'Wedge'
        ],
        correctAnswer: 2,
        explanation: 'The putter is used on every hole and can save or cost you the most strokes.',
        skill: 'strategy'
      }
    ],
    aiPrompts: [
      {
        id: 'golf-ai-1',
        title: 'Swing Analysis',
        prompt: 'Analyze this golf swing video and provide specific feedback on form, tempo, and areas for improvement.',
        context: 'User uploads a video of their golf swing or round',
        expectedOutput: 'Detailed analysis with specific tips, drills, and improvement areas'
      }
    ]
  },
  branding: {
    logo: '/assets/golf-logo.png',
    heroImage: '/assets/golf-hero.jpg',
    colorScheme: {
      primary: '#28A745',
      secondary: '#17A2B8',
      accent: '#FFC107',
      background: '#FFFFFF',
      surface: '#F8F9FA',
      text: '#2C3E50',
      textSecondary: '#6C757D',
      success: '#28A745',
      warning: '#FFC107',
      error: '#DC3545',
      border: '#E9ECEF'
    },
    typography: {
      fontFamily: 'Inter, system-ui, sans-serif',
      headingFont: 'Inter, system-ui, sans-serif',
      bodyFont: 'Inter, system-ui, sans-serif'
    }
  }
};

// Hiking Configuration
export const hikingConfig: SportConfig = {
  id: 'hiking',
  name: 'hiking',
  displayName: 'Hiking',
  primaryColor: '#5D4037',
  secondaryColor: '#2D6A4F',
  accentColor: '#2D6A4F',
  sportIcon: '🥾',
  description: 'Master the trails with expert hiking techniques, safety tips, and gear recommendations.',
  features: [
    'Trail Navigation',
    'Safety & First Aid',
    'Gear Recommendations',
    'Fitness Training',
    'Weather Preparation',
    'Leave No Trace'
  ],
  content: {
    drills: [
      {
        id: 'hiking-endurance',
        title: 'Endurance Building',
        description: 'Build stamina for long hikes',
        difficulty: 'beginner',
        duration: 30,
        equipment: ['Water bottle', 'Comfortable shoes'],
        instructions: [
          'Start with 15-minute walks on flat terrain',
          'Gradually increase distance each week',
          'Add elevation changes after 2 weeks',
          'Practice with a weighted pack'
        ],
        tips: [
          'Listen to your body and rest when needed',
          'Stay hydrated throughout the exercise',
          'Focus on steady, rhythmic breathing'
        ]
      },
      {
        id: 'hiking-balance',
        title: 'Balance & Stability',
        description: 'Improve balance on uneven terrain',
        difficulty: 'intermediate',
        duration: 20,
        equipment: ['Balance board or unstable surface'],
        instructions: [
          'Stand on one foot for 30 seconds',
          'Switch to the other foot',
          'Practice on uneven surfaces',
          'Add arm movements while balancing'
        ],
        tips: [
          'Start near a wall for support',
          'Focus on a fixed point ahead',
          'Engage your core muscles'
        ]
      }
    ],
    strategies: [
      {
        id: 'hiking-pace',
        title: 'Pacing Strategy',
        description: 'Maintain energy throughout your hike',
        tips: [
          'Start slower than you think you need to',
          'Take regular breaks every 45-60 minutes',
          'Monitor your breathing and heart rate',
          'Adjust pace based on terrain difficulty'
        ]
      },
      {
        id: 'hiking-navigation',
        title: 'Trail Navigation',
        description: 'Stay on course and avoid getting lost',
        tips: [
          'Study maps before starting your hike',
          'Use GPS and compass as backup',
          'Mark key landmarks along the way',
          'Always tell someone your planned route'
        ]
      }
    ],
    equipment: [
      {
        id: 'hiking-boots',
        name: 'Hiking Boots',
        description: 'Essential footwear for trail hiking',
        recommendations: [
          'Choose boots with good ankle support',
          'Ensure proper fit with hiking socks',
          'Break in boots before long hikes',
          'Consider waterproof options for wet conditions'
        ],
        maintenance: [
          'Clean boots after each hike',
          'Apply waterproofing treatment regularly',
          'Replace insoles when worn',
          'Store in a cool, dry place'
        ]
      },
      {
        id: 'hiking-backpack',
        name: 'Hiking Backpack',
        description: 'Carry your gear comfortably',
        recommendations: [
          'Choose appropriate size for hike duration',
          'Look for adjustable straps and hip belt',
          'Consider hydration system compatibility',
          'Test pack weight distribution'
        ],
        maintenance: [
          'Clean pack regularly',
          'Check zippers and buckles',
          'Repair tears immediately',
          'Store empty and unzipped'
        ]
      }
    ],
    skillAssessment: [
      {
        id: 'hiking-assessment-1',
        question: 'What is the most important safety rule when hiking?',
        options: [
          'Bring plenty of snacks',
          'Tell someone your planned route',
          'Wear the latest gear',
          'Hike alone for peace'
        ],
        correctAnswer: 1,
        explanation: 'Always tell someone your planned route and expected return time. This is crucial for safety in case of emergency.',
        skill: 'safety'
      },
      {
        id: 'hiking-assessment-2',
        question: 'What should you do if you encounter wildlife on the trail?',
        options: [
          'Run away quickly',
          'Make loud noises to scare it',
          'Stay calm and give it space',
          'Take photos for social media'
        ],
        correctAnswer: 2,
        explanation: 'Stay calm, avoid sudden movements, and give wildlife plenty of space. Most animals will move away if not threatened.',
        skill: 'safety'
      }
    ],
    aiPrompts: [
      {
        id: 'hiking-ai-1',
        title: 'Trail Video Analysis',
        prompt: 'Analyze this hiking video and provide feedback on technique, safety practices, and gear usage.',
        context: 'User uploads a video of their hiking experience',
        expectedOutput: 'Detailed analysis with safety tips, technique improvements, and gear recommendations'
      }
    ]
  },
  branding: {
    logo: '/assets/hiking-logo.png',
    heroImage: '/assets/hiking-hero.jpg',
    colorScheme: {
      primary: '#5D4037',
      secondary: '#2D6A4F',
      accent: '#2D6A4F',
      background: '#FFFFFF',
      surface: '#F8F9FA',
      text: '#2C3E50',
      textSecondary: '#6C757D',
      success: '#28A745',
      warning: '#FFC107',
      error: '#DC3545',
      border: '#E9ECEF'
    },
    typography: {
      fontFamily: 'Inter, system-ui, sans-serif',
      headingFont: 'Inter, system-ui, sans-serif',
      bodyFont: 'Inter, system-ui, sans-serif'
    }
  }
};

// Padel Configuration
export const padelConfig: SportConfig = {
  id: 'padel',
  name: 'Padel',
  displayName: 'Padel Coach',
  primaryColor: '#1E40AF',
  secondaryColor: '#3B82F6',
  accentColor: '#F8FAFC',
  sportIcon: '🏓',
  description: 'Master Padel with AI-powered coaching, video analysis, and personalized training plans.',
  features: [
    'video-analysis',
    'skill-assessment',
    'drill-library',
    'strategy-guide',
    'equipment-recommendations',
    'fundamentals-training',
    'ai-coaching',
    'progress-tracking'
  ],
  content: {
    drills: [
      {
        id: 'padel-drill-1',
        title: 'Wall Practice',
        description: 'Improve your basic strokes and consistency using a wall',
        difficulty: 'beginner',
        duration: 15,
        equipment: ['padel racket', 'padel ball', 'wall'],
        instructions: [
          'Stand 3-4 meters from a wall',
          'Practice forehand and backhand strokes',
          'Focus on consistent contact and control',
          'Vary the pace and height of your shots',
          'Try to maintain a rally with yourself'
        ],
        tips: [
          'Start with slow, controlled shots',
          'Focus on proper technique over power',
          'Keep your eye on the ball',
          'Use this drill to warm up before matches'
        ]
      },
      {
        id: 'padel-drill-2',
        title: 'Volley Practice',
        description: 'Develop quick reflexes and net play skills',
        difficulty: 'beginner',
        duration: 20,
        equipment: ['padel racket', 'padel ball', 'partner', 'net'],
        instructions: [
          'Stand at the net with your partner',
          'Practice volleying back and forth',
          'Start with slow, controlled volleys',
          'Gradually increase the pace',
          'Focus on keeping the ball in play'
        ],
        tips: [
          'Keep your racket head up',
          'Use short, compact swings',
          'Stay balanced and ready to move',
          'Practice both forehand and backhand volleys'
        ]
      }
    ],
    strategies: [
      {
        id: 'padel-strategy-1',
        title: 'Net Control Strategy',
        description: 'Dominate the net to control the point and create winning opportunities',
        level: 'intermediate',
        situation: 'When both teams are at the net',
        execution: [
          'Move to the net whenever possible',
          'Use volleys to keep opponents on the defensive',
          'Practice quick reflexes and positioning',
          'Communicate with your partner about net coverage'
        ],
        commonMistakes: [
          'Standing too far from the net',
          'Not communicating with partner',
          'Over-swinging on volleys'
        ]
      }
    ],
    equipment: [
      {
        id: 'padel-equipment-1',
        name: 'Padel Racket',
        description: 'The essential equipment for playing Padel - choose the right racket for your skill level',
        price: '$80-200',
        recommendations: [
          'Choose a racket weight that feels comfortable (300-370g)',
          'Consider the balance point (head-heavy for power, head-light for control)',
          'Select appropriate grip size for your hand',
          'Look for good build quality and durability'
        ],
        maintenance: [
          'Clean your racket after each use',
          'Store in a protective case',
          'Check for damage regularly',
          'Replace grip when it becomes slippery'
        ]
      }
    ],
    fundamentals: [
      {
        id: 'padel-fundamental-1',
        title: 'Padel Grip',
        description: 'Master the continental grip for optimal control and power in Padel',
        importance: 'critical',
        steps: [
          'Hold the racket with a continental grip (like shaking hands)',
          'Place your index finger slightly separated from the others',
          'Keep your grip relaxed but firm',
          'The grip should feel natural and comfortable'
        ],
        practiceTips: [
          'Practice this grip daily',
          'Use it for both forehand and backhand shots',
          'Make it your default grip'
        ]
      }
    ],
    skillAssessment: [
      {
        id: 'padel-assessment-1',
        question: 'What is the standard size of a Padel court?',
        options: [
          '20m x 10m',
          '18m x 9m',
          '22m x 11m',
          '16m x 8m'
        ],
        correctAnswer: 0,
        explanation: 'A standard Padel court is 20 meters long and 10 meters wide, with walls on all sides.',
        skill: 'rules'
      }
    ],
    aiPrompts: [
      {
        id: 'padel-ai-1',
        title: 'Video Analysis',
        prompt: 'Analyze this Padel video and provide specific feedback on technique, strategy, and areas for improvement.',
        context: 'User uploads a video of their Padel game or practice session',
        expectedOutput: 'Detailed analysis with specific tips, drills, and improvement areas'
      }
    ]
  },
  branding: {
    logo: '/assets/padel-logo.png',
    heroImage: '/assets/padel-hero.jpg',
    colorScheme: {
      primary: '#1E40AF',
      secondary: '#3B82F6',
      accent: '#F8FAFC',
      background: '#FFFFFF',
      surface: '#F8F9FA',
      text: '#2C3E50',
      textSecondary: '#6C757D',
      success: '#28A745',
      warning: '#FFC107',
      error: '#DC3545',
      border: '#E9ECEF'
    },
    typography: {
      fontFamily: 'Inter, system-ui, sans-serif',
      headingFont: 'Inter, system-ui, sans-serif',
      bodyFont: 'Inter, system-ui, sans-serif'
    }
  }
};

// Badminton Configuration
export const badmintonConfig: SportConfig = {
  id: 'badminton',
  name: 'Badminton',
  displayName: 'Badminton Coach',
  primaryColor: '#3730A3',
  secondaryColor: '#6366F1',
  accentColor: '#FFFFFF',
  sportIcon: '🏸',
  description: 'Master Badminton with AI-powered coaching, technique analysis, and personalized training plans.',
  features: [
    'video-analysis',
    'skill-assessment',
    'drill-library',
    'strategy-guide',
    'equipment-recommendations',
    'fundamentals-training',
    'ai-coaching',
    'progress-tracking'
  ],
  content: {
    drills: [
      {
        id: 'badminton-drill-1',
        title: 'Wall Rally Practice',
        description: 'Improve basic strokes and consistency using a wall',
        difficulty: 'beginner',
        duration: 15,
        equipment: ['badminton racket', 'shuttlecock', 'wall'],
        instructions: [
          'Stand 2-3 meters from a wall',
          'Practice forehand and backhand clears',
          'Focus on consistent contact and control',
          'Vary the pace and height of your shots',
          'Try to maintain a rally with yourself'
        ],
        tips: [
          'Start with slow, controlled shots',
          'Focus on proper technique over power',
          'Keep your eye on the shuttlecock',
          'Use this drill to warm up before matches'
        ]
      },
      {
        id: 'badminton-drill-2',
        title: 'Net Shot Practice',
        description: 'Develop precise net play and touch',
        difficulty: 'beginner',
        duration: 20,
        equipment: ['badminton racket', 'shuttlecock', 'net', 'partner'],
        instructions: [
          'Stand at the net with your partner',
          'Practice net shots back and forth',
          'Start with slow, controlled shots',
          'Focus on keeping the shuttlecock low',
          'Try to make it difficult for your partner'
        ],
        tips: [
          'Use a gentle touch',
          'Keep the shuttlecock close to the net',
          'Stay balanced and ready to move',
          'Practice both forehand and backhand net shots'
        ]
      },
      {
        id: 'badminton-drill-3',
        title: 'Clear and Drop Combination',
        description: 'Practice alternating between clears and drops',
        difficulty: 'intermediate',
        duration: 25,
        equipment: ['badminton racket', 'shuttlecock', 'partner'],
        instructions: [
          'Stand at opposite ends of the court',
          'Alternate between high clears and drop shots',
          'Focus on proper footwork and positioning',
          'Use the full court for variety',
          'Maintain consistent rhythm and timing'
        ],
        tips: [
          'Use your legs for power on clears',
          'Use wrist action for drop shots',
          'Stay balanced between shots',
          'Practice deception in your shots'
        ]
      }
    ],
    strategies: [
      {
        id: 'badminton-strategy-1',
        title: 'Court Positioning Strategy',
        description: 'Optimal positioning for different game situations',
        level: 'intermediate',
        situation: 'General court positioning',
        execution: [
          'Stay in the center of your half of the court',
          'Move forward for net shots and drops',
          'Move back for clears and smashes',
          'Always return to the center after each shot',
          'Anticipate your opponent\'s next shot'
        ],
        commonMistakes: [
          'Standing too close to the net',
          'Not returning to center position',
          'Poor footwork and positioning',
          'Not anticipating opponent\'s shots'
        ]
      },
      {
        id: 'badminton-strategy-2',
        title: 'Serve and Return Strategy',
        description: 'Strategic approach to serving and returning',
        level: 'beginner',
        situation: 'Serving and receiving serves',
        execution: [
          'Vary your serve placement and speed',
          'Use short serves to bring opponent forward',
          'Use long serves to push opponent back',
          'Return serves with purpose and placement',
          'Follow up your serve with good positioning'
        ],
        commonMistakes: [
          'Always serving to the same spot',
          'Not following up the serve',
          'Poor return placement',
          'Not varying serve strategy'
        ]
      }
    ],
    equipment: [
      {
        id: 'badminton-equipment-1',
        name: 'Badminton Racket',
        description: 'The essential equipment for playing Badminton - choose the right racket for your skill level',
        price: '$50-300',
        recommendations: [
          'Choose appropriate weight (80-100g for beginners)',
          'Consider string tension (18-24 lbs for beginners)',
          'Select grip size that feels comfortable',
          'Look for good balance and control',
          'Consider your playing style and skill level'
        ],
        maintenance: [
          'Clean your racket after each use',
          'Store in a protective case',
          'Check strings regularly for damage',
          'Replace grip when it becomes slippery',
          'Avoid extreme temperatures'
        ]
      },
      {
        id: 'badminton-equipment-2',
        name: 'Shuttlecocks',
        description: 'Choose the right shuttlecocks for practice and matches',
        price: '$10-50 per tube',
        recommendations: [
          'Use feather shuttlecocks for matches',
          'Use synthetic shuttlecocks for practice',
          'Choose appropriate speed (75, 76, 77, 78)',
          'Consider your playing environment',
          'Buy in bulk for better value'
        ],
        maintenance: [
          'Store in cool, dry place',
          'Handle feathers carefully',
          'Replace damaged shuttlecocks',
          'Use appropriate speed for conditions'
        ]
      }
    ],
    fundamentals: [
      {
        id: 'badminton-fundamental-1',
        title: 'Proper Grip and Stance',
        description: 'Fundamental setup and ready position for optimal play',
        importance: 'critical',
        steps: [
          'Use forehand grip for forehand shots',
          'Use backhand grip for backhand shots',
          'Keep racket up and ready at all times',
          'Stay balanced and on balls of feet',
          'Keep knees slightly bent for quick movement',
          'Maintain proper stance for instant reactions'
        ],
        practiceTips: [
          'Practice grip changes between shots',
          'Focus on balance and footwork',
          'Keep your eye on the shuttlecock',
          'Stay relaxed and ready to move'
        ]
      },
      {
        id: 'badminton-fundamental-2',
        title: 'Basic Stroke Technique',
        description: 'Fundamental stroke mechanics for all shots',
        importance: 'critical',
        steps: [
          'Use proper grip for each shot type',
          'Follow through in the direction of the shot',
          'Use your wrist for power and control',
          'Keep your eye on the shuttlecock',
          'Use your legs for power generation',
          'Maintain balance throughout the stroke'
        ],
        practiceTips: [
          'Start with slow, controlled movements',
          'Focus on technique before power',
          'Practice each stroke individually',
          'Use a mirror to check your form'
        ]
      }
    ],
    skillAssessment: [
      {
        id: 'badminton-assessment-1',
        question: 'What is the standard height of a Badminton net?',
        options: [
          '1.55 meters (5 feet 1 inch)',
          '1.52 meters (5 feet)',
          '1.58 meters (5 feet 2 inches)',
          '1.50 meters (4 feet 11 inches)'
        ],
        correctAnswer: 0,
        explanation: 'A standard Badminton net is 1.55 meters (5 feet 1 inch) high at the edges and 1.524 meters (5 feet) high at the center.',
        skill: 'rules'
      },
      {
        id: 'badminton-assessment-2',
        question: 'What is the maximum number of hits allowed per side in Badminton?',
        options: [
          '1 hit per side',
          '2 hits per side',
          '3 hits per side',
          'Unlimited hits'
        ],
        correctAnswer: 0,
        explanation: 'In Badminton, each side is allowed only 1 hit to return the shuttlecock over the net.',
        skill: 'rules'
      }
    ],
    aiPrompts: [
      {
        id: 'badminton-ai-1',
        title: 'Video Analysis',
        prompt: 'Analyze this Badminton video and provide specific feedback on technique, strategy, and areas for improvement.',
        context: 'User uploads a video of their Badminton game or practice session',
        expectedOutput: 'Detailed analysis with specific tips, drills, and improvement areas'
      }
    ]
  },
  branding: {
    logo: '/assets/badminton-logo.png',
    heroImage: '/assets/badminton-hero.jpg',
    colorScheme: {
      primary: '#3730A3',
      secondary: '#6366F1',
      accent: '#FFFFFF',
      background: '#FFFFFF',
      surface: '#F8F9FA',
      text: '#2C3E50',
      textSecondary: '#6C757D',
      success: '#28A745',
      warning: '#FFC107',
      error: '#DC3545',
      border: '#E9ECEF'
    },
    typography: {
      fontFamily: 'Inter, system-ui, sans-serif',
      headingFont: 'Inter, system-ui, sans-serif',
      bodyFont: 'Inter, system-ui, sans-serif'
    }
  }
};

// Export all configurations
export const racquetballConfig: SportConfig = {
  id: 'racquetball',
  name: 'Racquetball',
  displayName: 'Racquetball Coach',
  primaryColor: '#9D174D',
  secondaryColor: '#DB2777',
  accentColor: '#FCE7F3',
  sportIcon: '🏓',
  description: 'Master Racquetball with AI-powered coaching, technique analysis, and personalized training plans.',
  features: [
    'video-analysis',
    'skill-assessment',
    'drill-library',
    'strategy-guide',
    'equipment-recommendations',
    'fundamentals-training',
    'ai-coaching',
    'progress-tracking'
  ],
  content: {
    drills: [
      {
        id: 'racquetball-drill-1',
        title: 'Wall Rally Practice',
        description: 'Improve basic strokes and consistency using a wall',
        difficulty: 'beginner',
        duration: 15,
        equipment: ['racquetball racquet', 'racquetball', 'wall'],
        instructions: [
          'Stand 2-3 meters from a wall',
          'Practice forehand and backhand shots',
          'Focus on consistent contact and control',
          'Vary the pace and height of your shots',
          'Try to maintain a rally with yourself'
        ],
        tips: [
          'Start with slow, controlled shots',
          'Focus on proper technique over power',
          'Keep your eye on the ball',
          'Use this drill to warm up before matches'
        ]
      },
      {
        id: 'racquetball-drill-2',
        title: 'Kill Shot Practice',
        description: 'Develop powerful kill shots for winning points',
        difficulty: 'intermediate',
        duration: 20,
        equipment: ['racquetball racquet', 'racquetball', 'court'],
        instructions: [
          'Stand in the front court area',
          'Practice hitting kill shots to the front wall',
          'Focus on keeping the ball low',
          'Aim for the bottom 6 inches of the front wall',
          'Follow through with your swing'
        ],
        tips: [
          'Use your legs for power',
          'Keep the ball low and fast',
          'Practice both forehand and backhand kills',
          'Focus on accuracy over power initially'
        ]
      }
    ],
    strategies: [
      {
        id: 'racquetball-strategy-1',
        title: 'Court Positioning Strategy',
        description: 'Optimal positioning for different game situations',
        level: 'intermediate',
        situation: 'General court positioning',
        execution: [
          'Stay in the center of the court when possible',
          'Move forward for kill shots and passes',
          'Move back for ceiling balls and lobs',
          'Always return to center court after each shot',
          'Anticipate your opponent\'s next shot'
        ],
        commonMistakes: [
          'Standing too close to the front wall',
          'Not returning to center position',
          'Poor footwork and positioning',
          'Not anticipating opponent\'s shots'
        ]
      }
    ],
    equipment: [
      {
        id: 'racquetball-equipment-1',
        name: 'Racquetball Racquet',
        description: 'The essential equipment for playing Racquetball - choose the right racquet for your skill level',
        price: '$50-300',
        recommendations: [
          'Choose appropriate weight (160-180g for beginners)',
          'Consider string tension (30-35 lbs for beginners)',
          'Select grip size that feels comfortable',
          'Look for good balance and control',
          'Consider your playing style and skill level'
        ],
        maintenance: [
          'Clean your racquet after each use',
          'Store in a protective case',
          'Check strings regularly for damage',
          'Replace grip when it becomes slippery',
          'Avoid extreme temperatures'
        ]
      }
    ],
    fundamentals: [
      {
        id: 'racquetball-fundamental-1',
        title: 'Proper Grip and Stance',
        description: 'Fundamental setup and ready position for optimal play',
        importance: 'critical',
        steps: [
          'Use continental grip for versatility',
          'Keep racquet up and ready at all times',
          'Stay balanced and on balls of feet',
          'Keep knees slightly bent for quick movement',
          'Maintain proper stance for instant reactions'
        ],
        practiceTips: [
          'Practice grip changes between shots',
          'Focus on balance and footwork',
          'Keep your eye on the ball',
          'Stay relaxed and ready to move'
        ]
      }
    ],
    skillAssessment: [
      {
        id: 'racquetball-assessment-1',
        question: 'What is the standard size of a racquetball court?',
        options: [
          '20 feet wide by 40 feet long by 20 feet high',
          '22 feet wide by 44 feet long by 20 feet high',
          '20 feet wide by 40 feet long by 22 feet high',
          '22 feet wide by 44 feet long by 22 feet high'
        ],
        correctAnswer: 0,
        explanation: 'A standard racquetball court is 20 feet wide by 40 feet long by 20 feet high.',
        skill: 'rules'
      }
    ],
    aiPrompts: [
      {
        id: 'racquetball-ai-1',
        title: 'Video Analysis',
        prompt: 'Analyze this Racquetball video and provide specific feedback on technique, strategy, and areas for improvement.',
        context: 'User uploads a video of their Racquetball game or practice session',
        expectedOutput: 'Detailed analysis with specific tips, drills, and improvement areas'
      }
    ]
  },
  branding: {
    logo: '/assets/racquetball-logo.png',
    heroImage: '/assets/racquetball-hero.jpg',
    colorScheme: {
      primary: '#9D174D',
      secondary: '#DB2777',
      accent: '#FCE7F3',
      background: '#FFFFFF',
      surface: '#F8F9FA',
      text: '#2C3E50',
      textSecondary: '#6C757D',
      success: '#28A745',
      warning: '#FFC107',
      error: '#DC3545',
      border: '#E9ECEF'
    },
    typography: {
      fontFamily: 'Inter, system-ui, sans-serif',
      headingFont: 'Inter, system-ui, sans-serif',
      bodyFont: 'Inter, system-ui, sans-serif'
    }
  }
};

export const squashConfig: SportConfig = {
  id: 'squash',
  name: 'Squash',
  displayName: 'Squash Coach',
  primaryColor: '#166534',
  secondaryColor: '#22C55E',
  accentColor: '#FBBF24',
  sportIcon: '🏓',
  description: 'Master Squash with AI-powered coaching, technique analysis, and personalized training plans.',
  features: [
    'video-analysis',
    'skill-assessment',
    'drill-library',
    'strategy-guide',
    'equipment-recommendations',
    'fundamentals-training',
    'ai-coaching',
    'progress-tracking'
  ],
  content: {
    drills: [
      {
        id: 'squash-drill-1',
        title: 'Wall Rally Practice',
        description: 'Improve basic strokes and consistency using a wall',
        difficulty: 'beginner',
        duration: 15,
        equipment: ['squash racquet', 'squash ball', 'wall'],
        instructions: [
          'Stand 2-3 meters from a wall',
          'Practice forehand and backhand shots',
          'Focus on consistent contact and control',
          'Vary the pace and height of your shots',
          'Try to maintain a rally with yourself'
        ],
        tips: [
          'Start with slow, controlled shots',
          'Focus on proper technique over power',
          'Keep your eye on the ball',
          'Use this drill to warm up before matches'
        ]
      },
      {
        id: 'squash-drill-2',
        title: 'Drop Shot Practice',
        description: 'Develop precise drop shots for winning points',
        difficulty: 'intermediate',
        duration: 20,
        equipment: ['squash racquet', 'squash ball', 'court'],
        instructions: [
          'Stand in the front court area',
          'Practice hitting drop shots to the front wall',
          'Focus on keeping the ball low and close to the wall',
          'Aim for the bottom 6 inches of the front wall',
          'Follow through with your swing'
        ],
        tips: [
          'Use a gentle touch',
          'Keep the ball low and close to the wall',
          'Practice both forehand and backhand drops',
          'Focus on accuracy over power'
        ]
      }
    ],
    strategies: [
      {
        id: 'squash-strategy-1',
        title: 'Court Positioning Strategy',
        description: 'Optimal positioning for different game situations',
        level: 'intermediate',
        situation: 'General court positioning',
        execution: [
          'Stay in the center of the court when possible',
          'Move forward for drop shots and volleys',
          'Move back for lobs and drives',
          'Always return to center court after each shot',
          'Anticipate your opponent\'s next shot'
        ],
        commonMistakes: [
          'Standing too close to the front wall',
          'Not returning to center position',
          'Poor footwork and positioning',
          'Not anticipating opponent\'s shots'
        ]
      }
    ],
    equipment: [
      {
        id: 'squash-equipment-1',
        name: 'Squash Racquet',
        description: 'The essential equipment for playing Squash - choose the right racquet for your skill level',
        price: '$50-300',
        recommendations: [
          'Choose appropriate weight (120-140g for beginners)',
          'Consider string tension (25-30 lbs for beginners)',
          'Select grip size that feels comfortable',
          'Look for good balance and control',
          'Consider your playing style and skill level'
        ],
        maintenance: [
          'Clean your racquet after each use',
          'Store in a protective case',
          'Check strings regularly for damage',
          'Replace grip when it becomes slippery',
          'Avoid extreme temperatures'
        ]
      }
    ],
    fundamentals: [
      {
        id: 'squash-fundamental-1',
        title: 'Proper Grip and Stance',
        description: 'Fundamental setup and ready position for optimal play',
        importance: 'critical',
        steps: [
          'Use continental grip for versatility',
          'Keep racquet up and ready at all times',
          'Stay balanced and on balls of feet',
          'Keep knees slightly bent for quick movement',
          'Maintain proper stance for instant reactions'
        ],
        practiceTips: [
          'Practice grip changes between shots',
          'Focus on balance and footwork',
          'Keep your eye on the ball',
          'Stay relaxed and ready to move'
        ]
      }
    ],
    skillAssessment: [
      {
        id: 'squash-assessment-1',
        question: 'What is the standard size of a squash court?',
        options: [
          '18.5 feet wide by 32 feet long',
          '20 feet wide by 40 feet long',
          '22 feet wide by 44 feet long',
          '16 feet wide by 32 feet long'
        ],
        correctAnswer: 0,
        explanation: 'A standard squash court is 18.5 feet wide by 32 feet long.',
        skill: 'rules'
      }
    ],
    aiPrompts: [
      {
        id: 'squash-ai-1',
        title: 'Video Analysis',
        prompt: 'Analyze this Squash video and provide specific feedback on technique, strategy, and areas for improvement.',
        context: 'User uploads a video of their Squash game or practice session',
        expectedOutput: 'Detailed analysis with specific tips, drills, and improvement areas'
      }
    ]
  },
  branding: {
    logo: '/assets/squash-logo.png',
    heroImage: '/assets/squash-hero.jpg',
    colorScheme: {
      primary: '#166534',
      secondary: '#22C55E',
      accent: '#FBBF24',
      background: '#FFFFFF',
      surface: '#F8F9FA',
      text: '#2C3E50',
      textSecondary: '#6C757D',
      success: '#28A745',
      warning: '#FFC107',
      error: '#DC3545',
      border: '#E9ECEF'
    },
    typography: {
      fontFamily: 'Inter, system-ui, sans-serif',
      headingFont: 'Inter, system-ui, sans-serif',
      bodyFont: 'Inter, system-ui, sans-serif'
    }
  }
};

// Running Configuration
export const runningConfig: SportConfig = {
  id: 'running',
  name: 'Running',
  displayName: 'Running Coach',
  primaryColor: '#B91C1C',
  secondaryColor: '#FF7043',
  accentColor: '#FFAB91',
  sportIcon: '🏃',
  description: 'Improve your running with form analysis, injury prevention, and training strategies.',
  features: ['video-analysis', 'skill-assessment', 'drill-library', 'strategy-guide', 'equipment-recommendations', 'fundamentals-training', 'recovery-resources'],
  content: {
    drills: [{ id: 'run-1', title: 'Cadence Drills', description: 'Improve turnover', difficulty: 'beginner', duration: 15, equipment: [], instructions: [], tips: [] }],
    strategies: [{ id: 'run-s1', title: 'Pacing', description: 'Run at the right effort', level: 'beginner', situation: 'Training', execution: [], commonMistakes: [] }],
    equipment: [{ id: 'run-e1', name: 'Running Shoes', description: 'Proper footwear', price: '', recommendations: [], maintenance: [] }],
    fundamentals: [{ id: 'run-f1', title: 'Form', description: 'Efficient mechanics', importance: 'critical', steps: [], practiceTips: [] }],
    skillAssessment: [{ id: 'run-a1', question: 'Replace shoes every?', options: ['6 months', '300-500 miles', '1 year'], correctAnswer: 1, explanation: 'Mileage matters', skill: 'equipment' }],
    aiPrompts: [{ id: 'run-ai1', title: 'Form Analysis', prompt: 'Analyze running form', context: 'Video', expectedOutput: 'Feedback' }]
  },
  branding: {
    logo: '/assets/running-logo.png',
    heroImage: '/assets/running-hero.jpg',
    colorScheme: { primary: '#B91C1C', secondary: '#F97316', accent: '#F97316', background: '#FFFFFF', surface: '#F8F9FA', text: '#2C3E50', textSecondary: '#6C757D', success: '#28A745', warning: '#FFC107', error: '#DC3545', border: '#E9ECEF' },
    typography: { fontFamily: 'Inter, system-ui, sans-serif', headingFont: 'Inter, system-ui, sans-serif', bodyFont: 'Inter, system-ui, sans-serif' }
  }
};

// Cycling Configuration
export const cyclingConfig: SportConfig = {
  id: 'cycling',
  name: 'Cycling',
  displayName: 'Cycling Coach',
  primaryColor: '#0F4C81',
  secondaryColor: '#42A5F5',
  accentColor: '#90CAF9',
  sportIcon: '🚴',
  description: 'Improve your cycling with technique analysis, bike fit guidance, and training strategies.',
  features: ['video-analysis', 'skill-assessment', 'drill-library', 'strategy-guide', 'equipment-recommendations', 'fundamentals-training', 'recovery-resources'],
  content: {
    drills: [{ id: 'cyc-1', title: 'Cadence Drills', description: 'Optimal pedaling', difficulty: 'beginner', duration: 20, equipment: [], instructions: [], tips: [] }],
    strategies: [{ id: 'cyc-s1', title: 'Bike Fit', description: 'Proper fit', level: 'beginner', situation: 'Setup', execution: [], commonMistakes: [] }],
    equipment: [{ id: 'cyc-e1', name: 'Bicycle', description: 'Right bike', price: '', recommendations: [], maintenance: [] }],
    fundamentals: [{ id: 'cyc-f1', title: 'Pedaling', description: 'Efficient stroke', importance: 'critical', steps: [], practiceTips: [] }],
    skillAssessment: [{ id: 'cyc-a1', question: 'Optimal cadence?', options: ['60 rpm', '80-100 rpm', '120 rpm'], correctAnswer: 1, explanation: 'Efficiency', skill: 'technique' }],
    aiPrompts: [{ id: 'cyc-ai1', title: 'Form Analysis', prompt: 'Analyze cycling form', context: 'Video', expectedOutput: 'Feedback' }]
  },
  branding: {
    logo: '/assets/cycling-logo.png',
    heroImage: '/assets/cycling-hero.jpg',
    colorScheme: { primary: '#0F4C81', secondary: '#0284C7', accent: '#EAB308', background: '#FFFFFF', surface: '#F8F9FA', text: '#2C3E50', textSecondary: '#6C757D', success: '#28A745', warning: '#FFC107', error: '#DC3545', border: '#E9ECEF' },
    typography: { fontFamily: 'Inter, system-ui, sans-serif', headingFont: 'Inter, system-ui, sans-serif', bodyFont: 'Inter, system-ui, sans-serif' }
  }
};

// Swimming Configuration
export const swimmingConfig: SportConfig = {
  id: 'swimming',
  name: 'Swimming',
  displayName: 'Swimming Coach',
  primaryColor: '#005B96',
  secondaryColor: '#00ACC1',
  accentColor: '#4DD0E1',
  sportIcon: '🏊',
  description: 'Improve your swimming with stroke analysis, technique drills, and injury prevention.',
  features: ['video-analysis', 'skill-assessment', 'drill-library', 'strategy-guide', 'equipment-recommendations', 'fundamentals-training', 'recovery-resources'],
  content: {
    drills: [{ id: 'swim-1', title: 'Catch-Up Drill', description: 'Freestyle timing', difficulty: 'beginner', duration: 15, equipment: [], instructions: [], tips: [] }],
    strategies: [{ id: 'swim-s1', title: 'Technique', description: 'Quality over volume', level: 'beginner', situation: 'Training', execution: [], commonMistakes: [] }],
    equipment: [{ id: 'swim-e1', name: 'Goggles', description: 'Clear vision', price: '', recommendations: [], maintenance: [] }],
    fundamentals: [{ id: 'swim-f1', title: 'Body Position', description: 'Streamlined', importance: 'critical', steps: [], practiceTips: [] }],
    skillAssessment: [{ id: 'swim-a1', question: 'Most common swim injury?', options: ['Knee', 'Ankle', 'Shoulder'], correctAnswer: 2, explanation: 'Overhead motion', skill: 'injury' }],
    aiPrompts: [{ id: 'swim-ai1', title: 'Stroke Analysis', prompt: 'Analyze stroke', context: 'Video', expectedOutput: 'Feedback' }]
  },
  branding: {
    logo: '/assets/swimming-logo.png',
    heroImage: '/assets/swimming-hero.jpg',
    colorScheme: { primary: '#005B96', secondary: '#0EA5E9', accent: '#78E0EB', background: '#FFFFFF', surface: '#F8F9FA', text: '#2C3E50', textSecondary: '#6C757D', success: '#28A745', warning: '#FFC107', error: '#DC3545', border: '#E9ECEF' },
    typography: { fontFamily: 'Inter, system-ui, sans-serif', headingFont: 'Inter, system-ui, sans-serif', bodyFont: 'Inter, system-ui, sans-serif' }
  }
};

// Strength Configuration
export const strengthConfig: SportConfig = {
  id: 'strength',
  name: 'Strength',
  displayName: 'Strength Coach',
  primaryColor: '#262626',
  secondaryColor: '#525252',
  accentColor: '#991B1B',
  sportIcon: '💪',
  description: 'Build strength safely with form analysis, programming guidance, and injury prevention.',
  features: ['video-analysis', 'skill-assessment', 'drill-library', 'strategy-guide', 'equipment-recommendations', 'fundamentals-training', 'recovery-resources'],
  content: {
    drills: [{ id: 'str-1', title: 'Goblet Squat', description: 'Squat pattern', difficulty: 'beginner', duration: 15, equipment: [], instructions: [], tips: [] }],
    strategies: [{ id: 'str-s1', title: 'Progressive Overload', description: 'Get stronger', level: 'beginner', situation: 'Training', execution: [], commonMistakes: [] }],
    equipment: [{ id: 'str-e1', name: 'Weights', description: 'Resistance', price: '', recommendations: [], maintenance: [] }],
    fundamentals: [{ id: 'str-f1', title: 'Core Bracing', description: 'Protect spine', importance: 'critical', steps: [], practiceTips: [] }],
    skillAssessment: [{ id: 'str-a1', question: 'Prioritize?', options: ['Weight', 'Form', 'Speed'], correctAnswer: 1, explanation: 'Form first', skill: 'technique' }],
    aiPrompts: [{ id: 'str-ai1', title: 'Lift Analysis', prompt: 'Analyze lift form', context: 'Video', expectedOutput: 'Feedback' }]
  },
  branding: {
    logo: '/assets/strength-logo.png',
    heroImage: '/assets/strength-hero.jpg',
    colorScheme: { primary: '#262626', secondary: '#525252', accent: '#991B1B', background: '#FFFFFF', surface: '#F8F9FA', text: '#2C3E50', textSecondary: '#6C757D', success: '#28A745', warning: '#FFC107', error: '#DC3545', border: '#E9ECEF' },
    typography: { fontFamily: 'Inter, system-ui, sans-serif', headingFont: 'Inter, system-ui, sans-serif', bodyFont: 'Inter, system-ui, sans-serif' }
  }
};

export const sportConfigs = {
  pickleball: pickleballConfig,
  tennis: tennisConfig,
  golf: golfConfig,
  hiking: hikingConfig,
  padel: padelConfig,
  badminton: badmintonConfig,
  racquetball: racquetballConfig,
  squash: squashConfig,
  running: runningConfig,
  cycling: cyclingConfig,
  swimming: swimmingConfig,
  strength: strengthConfig
};

// Helper function to get current sport config
export const getCurrentSportConfig = (): SportConfig => {
  // This will be set based on environment or build configuration
  const sport = process.env.REACT_APP_SPORT || 'pickleball';
  return sportConfigs[sport as keyof typeof sportConfigs] || pickleballConfig;
};

// Helper function to get config by sport ID
export const getSportConfig = (sportId: string): SportConfig => {
  return sportConfigs[sportId as keyof typeof sportConfigs] || pickleballConfig;
};
